import { useState, useEffect, useCallback } from 'react';

interface ParallaxConfig {
  speed?: number; // 0.01 - 0.1 recommended
  maxOffset?: number; // max px movement
  mobileReduction?: number; // 0-1, default 0.3
}

export const useParallax = (config: ParallaxConfig = {}) => {
  const { 
    speed = 0.04, 
    maxOffset = 35, 
    mobileReduction = 0.3 
  } = config;
  
  const [scrollY, setScrollY] = useState(0);
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  useEffect(() => {
    let ticking = false;
    let lastScrollY = window.scrollY;

    const handleScroll = () => {
      lastScrollY = window.scrollY;
      
      if (!ticking) {
        window.requestAnimationFrame(() => {
          setScrollY(lastScrollY);
          ticking = false;
        });
        ticking = true;
      }
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const getParallaxOffset = useCallback((customSpeed?: number) => {
    const effectiveSpeed = customSpeed ?? speed;
    const mobileMultiplier = isMobile ? mobileReduction : 1;
    const offset = scrollY * effectiveSpeed * mobileMultiplier;
    return Math.min(Math.max(offset, -maxOffset), maxOffset);
  }, [scrollY, speed, maxOffset, isMobile, mobileReduction]);

  const getParallaxStyle = useCallback((customSpeed?: number) => {
    const offset = getParallaxOffset(customSpeed);
    return {
      transform: `translateY(${offset}px)`,
      transition: 'transform 0.15s ease-out',
    };
  }, [getParallaxOffset]);

  return {
    scrollY,
    isMobile,
    getParallaxOffset,
    getParallaxStyle,
  };
};

// Hook for element-specific parallax with ref
export const useElementParallax = (config: ParallaxConfig = {}) => {
  const { scrollY, isMobile, getParallaxOffset } = useParallax(config);
  const [elementTop, setElementTop] = useState(0);

  const setRef = useCallback((element: HTMLElement | null) => {
    if (element) {
      const rect = element.getBoundingClientRect();
      setElementTop(rect.top + window.scrollY);
    }
  }, []);

  const getRelativeOffset = useCallback((customSpeed?: number) => {
    const relativeScroll = scrollY - elementTop + window.innerHeight;
    if (relativeScroll < 0) return 0;
    
    const { speed = 0.04, maxOffset = 35, mobileReduction = 0.3 } = config;
    const effectiveSpeed = customSpeed ?? speed;
    const mobileMultiplier = isMobile ? mobileReduction : 1;
    const offset = relativeScroll * effectiveSpeed * mobileMultiplier;
    return Math.min(Math.max(offset, -maxOffset), maxOffset);
  }, [scrollY, elementTop, config, isMobile]);

  return {
    setRef,
    getRelativeOffset,
    scrollY,
  };
};
